import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Coins, Check, Sparkles, TrendingUp, Gift } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { usePayments } from '@/hooks/usePayments';
import { useAuth } from '@/contexts/AuthContext';
import { usePricingRates } from '@/hooks/usePricingRates';
import { creditsToMinutes } from '@/utils/creditConversionUtils';

interface CreditPackage {
  id: string;
  name: string;
  credits: number;
  bonus_credits: number;
  total_price: number;
  price_per_credit: number;
  stripe_price_id: string | null;
  is_active: boolean;
}

export const CreditPurchasePanel = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const { purchaseCredits, isProcessing } = usePayments();
  const [purchasingPackageId, setPurchasingPackageId] = useState<string | null>(null);
  const { audioRate, tier } = usePricingRates();

  const { data: packages = [], isLoading } = useQuery({
    queryKey: ['credit-packages'],
    queryFn: async () => {
      // Fetch from backend API instead of Supabase
      const response = await fetch(`${import.meta.env.VITE_BACKEND_URL}/payments/credit-packages`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch credit packages');
      }

      const data = await response.json();
      return data as CreditPackage[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch user's profile to check if they have a referral code applied
  const { data: userProfile, isLoading: profileLoading } = useQuery({
    queryKey: ['user-profile-referral', user?.id],
    queryFn: async () => {
      console.log('🔍 Fetching user profile for referral check...');

      // Use backend /auth/me endpoint
      const response = await fetch(`${import.meta.env.VITE_BACKEND_URL}/auth/me`, {
        credentials: 'include',
      });

      if (!response.ok) {
        console.error('❌ Failed to fetch user profile:', response.status);
        return null;
      }

      const data = await response.json();
      console.log('✅ User profile fetched:', {
        hasReferralCode: !!data?.referral_code_id,
        referralCodeId: data?.referral_code_id
      });
      return data;
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  // Check if user has a referral code applied
  const hasReferralCode = userProfile?.referral_code_id;

  // For now, we'll show a general discount message
  // In the future, we can fetch the exact discount percentage from the referral code
  const referralDiscountPercentage = hasReferralCode ? 10 : null; // Default to 10% if has referral

  console.log('💰 Credit Purchase Panel State:', {
    user: !!user,
    userProfile: !!userProfile,
    hasReferralCode,
    referralDiscountPercentage,
    profileLoading
  });

  const handlePurchase = async (pkg: CreditPackage) => {
    setPurchasingPackageId(pkg.id);

    try {
      // Initiate Stripe checkout
      await purchaseCredits(pkg.id);
      // Note: User will be redirected to Stripe, so no cleanup needed
    } catch (error) {
      console.error('Purchase error:', error);
      setPurchasingPackageId(null);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Purchase Credits</CardTitle>
          <CardDescription>Loading credit packages...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-32 bg-muted rounded-lg"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const getPopularPackageId = () => {
    // Mark the package with best value (highest bonus percentage) as popular
    if (packages.length === 0) return null;
    const bestValuePkg = packages.reduce((best, current) => {
      const currentBonus = (current.bonus_credits / current.credits) * 100;
      const bestBonus = (best.bonus_credits / best.credits) * 100;
      return currentBonus > bestBonus ? current : best;
    });
    return bestValuePkg.id;
  };

  const popularPackageId = getPopularPackageId();

  const getBonusPercentage = (pkg: CreditPackage) => {
    if (pkg.bonus_credits === 0) return 0;
    return Math.round((pkg.bonus_credits / pkg.credits) * 100);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Coins className="h-5 w-5 text-primary" />
          Purchase Credits
        </CardTitle>
        <CardDescription>
          Choose a credit package to continue creating personalized sessions. Larger packages include bonus credits!
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Referral Discount Notice */}
        {hasReferralCode && referralDiscountPercentage && (
          <div className="mb-6 p-4 rounded-lg bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 border-2 border-green-200 dark:border-green-800">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 dark:bg-green-800 flex items-center justify-center">
                <Gift className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-green-900 dark:text-green-100 mb-1">
                  Referral Discount Active!
                </h4>
                <p className="text-sm text-green-800 dark:text-green-200">
                  You're receiving <span className="font-bold">{referralDiscountPercentage}% off</span> all credit purchases because you used a referral code.
                  The discount is automatically applied at checkout.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-2">
          {packages.map((pkg) => {
            const totalCredits = pkg.credits + pkg.bonus_credits;
            const bonusPercentage = getBonusPercentage(pkg);
            const isPopular = pkg.id === popularPackageId;
            const isPurchasing = purchasingPackageId === pkg.id;

            // Calculate discounted price if user has referral discount
            const originalPrice = pkg.total_price;
            const discountedPrice = referralDiscountPercentage
              ? originalPrice * (1 - referralDiscountPercentage / 100)
              : originalPrice;
            const hasDiscount = referralDiscountPercentage && referralDiscountPercentage > 0;

            return (
              <Card
                key={pkg.id}
                className={`relative ${
                  isPopular ? 'border-primary shadow-lg ring-2 ring-primary/20' : ''
                }`}
              >
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-gradient-to-r from-primary to-primary-glow text-primary-foreground">
                      <Sparkles className="h-3 w-3 mr-1" />
                      Best Value
                    </Badge>
                  </div>
                )}

                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>{pkg.name}</span>
                    {bonusPercentage > 0 && (
                      <Badge variant="secondary">
                        <TrendingUp className="h-3 w-3 mr-1" />
                        +{bonusPercentage}%
                      </Badge>
                    )}
                  </CardTitle>
                  <CardDescription>
                    <div className="flex items-baseline gap-2">
                      {hasDiscount && (
                        <span className="text-lg font-medium text-muted-foreground line-through">
                          ${originalPrice.toFixed(2)}
                        </span>
                      )}
                      <span className={`text-3xl font-bold ${hasDiscount ? 'text-green-600 dark:text-green-400' : 'text-foreground'}`}>
                        ${discountedPrice.toFixed(2)}
                      </span>
                      {hasDiscount && (
                        <Badge variant="secondary" className="bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300">
                          -{referralDiscountPercentage}%
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      ${(hasDiscount ? discountedPrice / (pkg.credits + pkg.bonus_credits) : pkg.price_per_credit).toFixed(2)} per credit
                    </div>
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center text-sm">
                      <Check className="h-4 w-4 text-primary mr-2" />
                      <span>{pkg.credits} credits</span>
                    </div>
                    {pkg.bonus_credits > 0 && (
                      <div className="flex items-center text-sm">
                        <Check className="h-4 w-4 text-primary mr-2" />
                        <span className="font-medium text-primary">
                          +{pkg.bonus_credits} bonus credits
                        </span>
                      </div>
                    )}
                    <div className="flex items-center text-sm font-medium">
                      <Coins className="h-4 w-4 text-primary mr-2" />
                      <span>Total: {totalCredits} credits</span>
                    </div>
                  </div>

                  <div className="pt-2">
                    <p className="text-xs text-muted-foreground mb-3">
                      {audioRate > 0 ? (
                        <>
                          <span className="font-semibold text-primary">
                            {totalCredits} credits → ~{Math.floor(creditsToMinutes(totalCredits, audioRate) / 3)} (3-minute) sessions
                          </span>
                          <span className="block text-xs mt-1">
                            (~{creditsToMinutes(totalCredits, audioRate)} minutes total at {audioRate} credits/min)
                          </span>
                        </>
                      ) : (
                        `${totalCredits} credits included`
                      )}
                    </p>
                    <Button
                      className="w-full"
                      variant={isPopular ? "default" : "outline"}
                      onClick={() => handlePurchase(pkg)}
                      disabled={isPurchasing || isProcessing}
                    >
                      {isPurchasing ? 'Redirecting to payment...' : `Purchase ${pkg.name}`}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {packages.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <Coins className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No credit packages available at this time.</p>
            <p className="text-sm">Please contact support for assistance.</p>
          </div>
        )}

        <div className="mt-6 p-4 bg-muted/50 rounded-lg">
          <h4 className="font-medium text-sm mb-2">💡 About Credits</h4>
          <ul className="text-xs text-muted-foreground space-y-1">
            <li>• Credits never expire - use them whenever you want</li>
            <li>• {audioRate > 0 ? `Your rate: ${audioRate} credits per minute (${tier} tier)` : 'Personalized pricing based on your tier'}</li>
            <li>• Individual users: 2 credits/min (Free) or 1.5 credits/min (Pro)</li>
            <li>• Practitioner users: 4 credits/min (Free) or 3 credits/min (Pro)</li>
            <li>• Larger packages include bonus credits for better value</li>
            <li>• Automatic refund if generation fails</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};
